import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Markdown, { Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { InsightsChatMessage } from 'contextProviders/InsightsChatProvider/conversationTypes';

import InsightsMessageChart from './InsightsMessageChart';
import InsightsMessageSource from './InsightsMessageSource';
import InsightsProposedAction from './InsightsProposedAction';
import InsightsStudioEdits from './InsightsStudioEdits';
import {
  buildCitationComponents,
  remarkInsightsCitations,
} from './insightsCitations';
import { stripCtxFooter } from './stripCtxFooter';

const MarkdownDiv = styled.div`
  color: var(--color-text-primary);

  p,
  li,
  td,
  th {
    color: var(--color-text-primary);
  }

  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }

  code {
    background-color: var(--color-bg-tertiary);
    color: var(--color-text-secondary);
    border-radius: 4px;
    padding: 2px 6px;
  }

  td,
  th {
    border: 1px solid var(--color-border-secondary);
    padding: 6px 10px;
    text-align: left;
  }
`;

// The rendered body of a settled assistant answer: prose (with inline citation
// markers), any charts, the auditable Sources footer, and a proposed build. The
// prose's [n] markers and the footer are coupled — clicking a marker expands the
// footer and highlights its source — so this component owns that shared state.
const InsightsAnswerBody = ({ message }: { message: InsightsChatMessage }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const sourceRef = useRef<HTMLDivElement>(null);

  // Only linkify markers that a real source carries, so a bracketed number the
  // prose happens to contain is never mistaken for a citation.
  const validIndices = useMemo(
    () =>
      new Set(
        (message.sources ?? [])
          .map((source) => source.citationIndex)
          .filter((n): n is number => typeof n === 'number'),
      ),
    [message.sources],
  );

  const onCiteClick = useCallback((index: number) => {
    setExpanded(true);
    setFocusedIndex(index);
    // Scroll after the footer has expanded to a real height.
    requestAnimationFrame(() =>
      sourceRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      }),
    );
  }, []);

  const remarkPlugins = useMemo<Options['remarkPlugins']>(
    () => [remarkGfm, [remarkInsightsCitations, { validIndices }]],
    [validIndices],
  );
  const components = useMemo(
    () => buildCitationComponents({ onCiteClick }),
    [onCiteClick],
  );

  // The stored content keeps the model-facing ctx footer for history replay;
  // strip it here so the customer never sees the raw marker.
  const visibleContent = useMemo(
    () => stripCtxFooter(message.content),
    [message.content],
  );

  return (
    <>
      <MarkdownDiv>
        <Markdown remarkPlugins={remarkPlugins} components={components}>
          {visibleContent}
        </Markdown>
      </MarkdownDiv>
      {/* A confirmed metric's block is the proposal card's to show */}
      <InsightsStudioEdits
        edits={message.studioEdits?.filter(
          (e) =>
            !message.proposedAction ||
            e.editId !== `edit_${message.proposedAction.id}`,
        )}
      />
      {message.charts?.map((spec, index) => (
        <InsightsMessageChart key={index} spec={spec} />
      ))}
      {!!message.sources?.length && (
        <InsightsMessageSource
          ref={sourceRef}
          sources={message.sources}
          expanded={expanded}
          onToggle={setExpanded}
          focusedIndex={focusedIndex}
        />
      )}
      {message.proposedAction && (
        <InsightsProposedAction
          messageId={message.id}
          action={message.proposedAction}
          status={message.proposedActionStatus ?? 'pending'}
          studioEdits={message.studioEdits}
        />
      )}
    </>
  );
};

export default InsightsAnswerBody;
